import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { User, UserPlus, Camera, Loader2 } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { BASE_URL } from '@/config/api';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";

interface Teacher {
  id: string;
  firstname: string;
  lastname: string;
  email: string;
  is_active: boolean;
  profile_picture?: string;
  has_user_account?: boolean;
  invitation_date?: string;
  username?: string;
  bg_color_agenda?: string;
}

interface EditTeacherFormProps {
  teacher: Teacher;
  teachers: Teacher[];
  onSubmit: (firstname: string, lastname: string, email: string, profile_picture?: File, bg_color_agenda?: string, isInvite?: boolean) => void;
  onCancel: () => void;
  sendInvite: (teacherId: string) => void;
  onOpenPricesDialog: () => void;
  onSettingChange?: (teacherId: string, canViewOthers: boolean, canViewTeacherInvoice: boolean, selectedTeacherIds: string[]) => void;
  isSubmitting?: boolean;
}

const EditTeacherForm: React.FC<EditTeacherFormProps> = ({
  teacher,
  teachers,
  onSubmit,
  onCancel,
  sendInvite,
  onOpenPricesDialog,
  onSettingChange,
  isSubmitting = false
}) => {
  const { token } = useAuth();
  const [firstname, setFirstname] = useState(teacher.firstname || '');
  const [lastname, setLastname] = useState(teacher.lastname || '');
  const [email, setEmail] = useState(teacher.email || '');
  const [username, setUsername] = useState(teacher.username || '');
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | undefined>(teacher.profile_picture);
  const [invitationSent, setInvitationSent] = useState<string | null>(null);
  const [isInvite, setIsInvite] = useState(false);
  const { t, locale } = useLanguage();
  const [bg_color_agenda, setColor] = useState(teacher.bg_color_agenda);
  const isMobile = useIsMobile();
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const invitation_date = teacher.invitation_date ? new Date(teacher.invitation_date) : null;
  const [canViewOthers, setCanViewOthers] = useState(false);
  const [canViewTeacherInvoice, setCanViewTeacherInvoice] = useState(false);
  const { user } = useAuth();
  const [selectedTeacherIds, setSelectedTeacherIds] = useState<string[]>([]);
  const [acceptedTeacherIds, setAcceptedTeacherIds] = useState<string[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const hasInitialized = React.useRef(false);

  const toggleTeacher = (id: string) => {
    setSelectedTeacherIds(prev => {
      const currentIds = Array.isArray(prev) ? prev : [];
      return currentIds.includes(id)
        ? currentIds.filter(tid => tid !== id)
        : [...currentIds, id];
    });
  };

  useEffect(() => {
    const fetchTeacherSettings = async () => {
      if (!token || !teacher.id || !user.school_id) return;
      if (hasInitialized.current) return;

      try {
        const response = await fetch(`${BASE_URL}/api/teachers/settings/${teacher.id}/${user.school_id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
        });

        if (response.ok) {
          const data = await response.json();
          const loadedIds = Array.isArray(data?.teacherIds)
            ? data.teacherIds
            : typeof data?.teacherIds === 'string'
              ? JSON.parse(data.teacherIds)
              : [];
          setSelectedTeacherIds(loadedIds);
          setAcceptedTeacherIds(loadedIds);
          setCanViewOthers(data?.can_view_others || false);
          setCanViewTeacherInvoice(data?.can_view_teacher_invoice || false);
          hasInitialized.current = true;
        }
      } catch (error) {
        console.error(error);
      }
    };

    fetchTeacherSettings();
  }, [token, teacher.id, user.school_id]);

  const handleInviteClick = () => {
    sendInvite(teacher.id);
    const now = new Date();
    const options = locale === 'fr' ? { hour: '2-digit', minute: '2-digit' } : { hour: 'numeric', minute: 'numeric', hour12: true };
    const time = now.toLocaleTimeString(locale);
    setInvitationSent(`${t('common.sendAt')} ${time}`);
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target && typeof event.target.result === 'string') {
          setAvatarPreview(event.target.result);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCanViewOthersChange = (checked: boolean) => {
    setCanViewOthers(checked);
    if (!checked) {
      setCanViewTeacherInvoice(false);
    }
  };

  const handleCanViewTeacherInvoiceChange = (checked: boolean) => {
    setCanViewTeacherInvoice(checked);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (firstname.trim() && lastname.trim()) {
      onSubmit(firstname.trim(), lastname.trim(), email.trim(), avatarFile, bg_color_agenda, isInvite);
      onSettingChange?.(teacher.id, canViewOthers, canViewTeacherInvoice, [...selectedTeacherIds]);
    }
  };

  return (
    <form onSubmit={handleSubmit} className={`${!isMobile ? 'h-auto' : 'h-full'} flex flex-col bg-white`}>
      <div className={`flex-grow overflow-auto ${!isMobile ? 'max-h-[60vh]' : 'p-3'} pt-2`}>
        {/* Header */}
        <div className="rounded bg-muted/30 rounded-t-xl p-2">
          <div className="flex items-center gap-6">
            <div className="relative shrink-0">
              <Avatar className="h-20 w-20 ring-4 ring-white shadow-sm cursor-pointer" onClick={handleAvatarClick}>
                {avatarPreview ? <AvatarImage src={avatarPreview} /> : <AvatarFallback className="bg-primary/10 text-primary text-3xl font-semibold">{firstname?.[0]}{lastname?.[0]}</AvatarFallback>}
              </Avatar>
              <div className="absolute bottom-2 right-2 bg-primary rounded-full p-1.5 shadow">
                <Camera className="h-4 w-4 text-white cursor-pointer" />
              </div>
              <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleFileChange} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <h2 className="text-xl font-semibold leading-tight">{firstname} {lastname}</h2>
                {teacher.has_user_account ? (
                  <Badge className="text-xs bg-emerald-100 text-emerald-700 px-3 py-1 text-xs font-medium">✓ {t('teachers.registered')}</Badge>
                ) : (
                  <Badge className="text-xs bg-amber-100 text-amber-700 px-3 py-1 text-xs font-medium">{t('teachers.noAccount')}</Badge>
                )}
              </div>
              {teacher.has_user_account && teacher.username && (
                <div className="flex items-center gap-2 mt-2 text-sm text-muted-foreground bg-gray-100 rounded-md px-3 py-2 w-max">
                  <User size={14} />
                  <span>{teacher.username}</span>
                </div>
              )}
              {!teacher.has_user_account && (
                <div className="mt-3 flex items-center gap-4 flex-wrap">
                  {invitation_date && <span className="text-xs text-muted-foreground">{t('teachers.invitationAt')} {invitation_date.toLocaleDateString(locale)}</span>}
                  {email && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" className="h-8">
                          <UserPlus size={14} className="mr-2" />
                          {t('teachers.sendInvite')}
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>{t('teachers.inviteTeacherTitle')}</AlertDialogTitle>
                          <AlertDialogDescription>{t('teachers.inviteTeacherDescription')}</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                          <AlertDialogAction onClick={handleInviteClick}>{t('common.ok')}</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                  {invitationSent && <span className="text-xs text-emerald-600">{invitationSent}</span>}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="m-1">
          <Tabs defaultValue="info" className="w-full">
            <TabsList className="grid grid-cols-2 w-full mt-4">
              <TabsTrigger value="info">Informations</TabsTrigger>
              <TabsTrigger value="settings">{t('teachers.customSettings')}</TabsTrigger>
            </TabsList>

            {/* Info Tab */}
            <TabsContent value="info">
              <div className="mt-4">
                 <div className="flex items-center gap-4 bg-gray-50 p-4 rounded-md">
                  <Input type="color" value={bg_color_agenda} onChange={(e) => setColor(e.target.value)} className="w-10 h-10 p-1 rounded-md cursor-pointer" />
                  <div><Label>{t('categories.color')}</Label></div>
                </div>
                <div className="mt-3">
                  <Label>{t('teachers.firstName')}</Label>
                  <Input value={firstname} onChange={(e) => setFirstname(e.target.value)} />
                </div>
                <div className="mt-4">
                  <Label>{t('teachers.lastName')}</Label>
                  <Input value={lastname} onChange={(e) => setLastname(e.target.value)} />
                </div>
                <div className="mt-4">
                  <Label>{t('teachers.email')}</Label>
                  <Input value={email} onChange={(e) => setEmail(e.target.value)} />
                </div>
                <div className="mt-4 flex items-center space-x-2">
                  <Checkbox checked={isInvite} onCheckedChange={(v) => setIsInvite(v as boolean)} />
                  <Label>{t('teachers.sendInvite')}</Label>
                </div>
              </div>
            </TabsContent>

            {/* Settings Tab */}
            <TabsContent value="settings">
              <div className="mt-4 space-y-4">
               
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center space-x-2 bg-gray-50 p-4 rounded-md">
                    <Switch checked={canViewOthers} onCheckedChange={handleCanViewOthersChange} />
                    <span className='text-xs'>{t('teachers.canViewOthers')}</span>
                  </div>
                  {canViewOthers && (
                    <div className="flex items-center space-x-2 bg-gray-50 p-4 rounded-md">
                      <Switch checked={canViewTeacherInvoice} onCheckedChange={handleCanViewTeacherInvoiceChange} />
                      <span className='text-xs'>{t('teachers.canViewTeacherInvoice')}</span>
                    </div>
                  )}
                </div>

                {canViewOthers && (
                  <div className="bg-gray-50 p-4 rounded-md">
                    <div className="flex items-center justify-between mb-3">
                      <span className='text-xs'>{t('teachers.associatedTeachers')}</span>
                      <button
                        type="button"
                        className="text-xs text-muted-foreground hover:text-black"
                        onClick={() => {
                          const allTeacherIds = teachers.map(t => t.id.toString()).filter(id => id !== teacher.id.toString());
                          setSelectedTeacherIds(
                            selectedTeacherIds.length === allTeacherIds.length
                              ? []
                              : allTeacherIds
                          );
                        }}
                      >
                        {selectedTeacherIds.length === teachers.length ? t('common.deselectAll') : t('common.selectAll')}
                      </button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                     {teachers.map((t) => {
                        if (t.id === teacher.id) {
                          return null;
                        }
                        const checked = selectedTeacherIds.includes(t.id.toString()); // ✅ Vérifie avec toString()
                        const checkboxId = `teacher-${t.id}`;
                        return (
                          <div
                            key={t.id}
                            className={`flex items-center gap-3 p-2 rounded-md border transition ${
                              checked ? "bg-primary/10 border-primary/30" : "bg-white"
                            }`}
                          >
                            <Checkbox
                              id={checkboxId}
                              checked={checked}
                              onCheckedChange={() => toggleTeacher(t.id.toString())} // ✅ Passe un string
                            />
                            <label htmlFor={checkboxId} className="text-sm cursor-pointer flex-1 select-none">
                              {t.firstname} {t.lastname}
                            </label>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Footer */}
      <div className={`flex gap-2 pt-4 mt-4 border-t ${isMobile ? 'fixed bottom-0 left-0 right-0 bg-gray-50 p-4 z-50 justify-center' : 'justify-end'}`}>
        <Button type="button" variant="outline" onClick={onCancel}>{t('common.cancel')}</Button>
        <Button type="button" variant="outline" onClick={onOpenPricesDialog}>{t('teachers.customizePrices')}</Button>
        <Button type="submit" disabled={!firstname.trim() || !lastname.trim() || isSubmitting}>
          {isSubmitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t('common.processing')}</> : t('common.done')}
        </Button>
      </div>
    </form>
  );
};

export default EditTeacherForm;